import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Pencil,
  Trash2,
  LogOut,
  Loader2,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  History,
  FileDown,
  Sun,
  Moon,
  Mail,
  Copy,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Combobox } from '../components/ui/combobox';
import { Switch } from '../components/ui/switch';
import Logo from '../components/Logo';
import { adminFetch, AdminAuthError, ADMIN_BASE_PATH, ADMIN_TOKEN_KEY } from '../lib/adminApi';
import { COUNTRIES } from '../lib/countries';
import { COUNTRY_CODES } from '../lib/countryCodes';
import { exportWorkbookToExcel } from '../lib/exportExcel';
import { useTheme } from '../hooks/useTheme';

// Mirrors backend/app/models/application.py's STAGE_FIELDS - keep in sync.
const STAGE_FIELDS = [
  ['application_received_date', 'Application Received'],
  ['document_verification_started_date', 'Started Document Verification'],
  ['preparation_started_date', 'Preparing for Submission'],
  ['work_permit_submitted_date', 'Work Permit Submitted for Processing'],
  ['work_permit_processing_date', 'Work Permit Under Processing'],
  ['work_permit_approved_date', 'Work Permit Approved'],
  ['visa_preparation_started_date', 'Document Preparation for Visa Started'],
  ['visa_application_started_date', 'Visa Application Started'],
];

const EMPTY_FORM = {
  file_number: '',
  passport_number: '',
  full_name: '',
  nationality: '',
  vendor: '',
  client: '',
  notes: '',
  email: '',
  phone_country_code: '',
  phone_number: '',
  address: '',
  ...Object.fromEntries(STAGE_FIELDS.map(([key]) => [key, ''])),
};

const EMPTY_ENTITY_FORM = { name: '', country: '', person_of_contact: '', email: '', phone_number: '' };
const EMPTY_VENDOR_FORM = {
  name: '',
  country: '',
  username: '',
  password: '',
  is_active: true,
  person_of_contact: '',
  email: '',
  phone_number: '',
};

const PAGE_SIZE = 10;

// Column key -> field on the application object. Only these three are
// sortable, per the admin's request.
const SORTABLE_COLUMNS = [
  ['file_number', 'File No.'],
  ['full_name', 'Name'],
  ['passport_number', 'Passport No.'],
];

const GROUPABLE_COLUMNS = [
  ['none', 'None'],
  ['nationality', 'Nationality'],
  ['vendor', 'Vendor'],
  ['client', 'Client'],
];

const UNASSIGNED_GROUP_LABEL = 'Not Assigned';

const LEAD_SORTABLE_COLUMNS = [
  ['full_name', 'Name'],
  ['email', 'Email'],
  ['created_at', 'Submitted'],
];

const LEAD_GROUPABLE_COLUMNS = [
  ['none', 'None'],
  ['partnership_type', 'Type'],
  ['status', 'Status'],
];

const STATUS_OPTIONS = ['new', 'contacted', 'approved', 'rejected'];

const currentStageLabel = (application) => {
  let label = null;
  STAGE_FIELDS.forEach(([key, stageLabel]) => {
    if (application[key]) label = stageLabel;
  });
  return label || 'Not started';
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const ApplicationFormFields = ({ values, onChange, vendorOptions, clientOptions }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <div className="space-y-1.5">
      <Label>File Number</Label>
      <Input
        value={values.file_number}
        onChange={(e) => onChange('file_number', e.target.value)}
        placeholder="Assigned by admin"
      />
    </div>
    <div className="space-y-1.5">
      <Label>Passport Number</Label>
      <Input value={values.passport_number} onChange={(e) => onChange('passport_number', e.target.value)} required />
    </div>
    <div className="space-y-1.5">
      <Label>Full Name</Label>
      <Input value={values.full_name} onChange={(e) => onChange('full_name', e.target.value)} required />
    </div>
    <div className="space-y-1.5">
      <Label>Nationality</Label>
      <Combobox
        value={values.nationality}
        onChange={(value) => onChange('nationality', value)}
        options={COUNTRIES}
        placeholder="Select country..."
        emptyText="No matching country."
      />
    </div>
    <div className="space-y-1.5 sm:col-span-2">
      <Label>Vendor</Label>
      <Combobox
        value={values.vendor}
        onChange={(value) => onChange('vendor', value)}
        options={vendorOptions}
        placeholder="Select or type a new vendor..."
        emptyText="No matching vendor."
        allowCustomValue
        clearable
      />
    </div>
    <div className="space-y-1.5 sm:col-span-2">
      <Label>Client</Label>
      <Combobox
        value={values.client}
        onChange={(value) => onChange('client', value)}
        options={clientOptions}
        placeholder="Select or type a new client..."
        emptyText="No matching client."
        allowCustomValue
        clearable
      />
    </div>
    {STAGE_FIELDS.map(([key, label]) => (
      <div className="space-y-1.5" key={key}>
        <Label>{label}</Label>
        <Input type="date" value={values[key]} onChange={(e) => onChange(key, e.target.value)} />
      </div>
    ))}
    <div className="space-y-1.5">
      <Label>Email</Label>
      <Input
        type="email"
        value={values.email}
        onChange={(e) => onChange('email', e.target.value)}
        placeholder="candidate@example.com"
      />
    </div>
    <div className="space-y-1.5">
      <Label>Phone</Label>
      <div className="flex gap-2">
        <Select
          value={values.phone_country_code}
          onValueChange={(value) => onChange('phone_country_code', value)}
        >
          <SelectTrigger className="w-36 shrink-0">
            <SelectValue placeholder="Code" />
          </SelectTrigger>
          <SelectContent>
            {COUNTRY_CODES.map((code) => (
              <SelectItem key={code.label} value={code.value}>
                {code.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={values.phone_number}
          onChange={(e) => onChange('phone_number', e.target.value)}
          placeholder="Phone number"
        />
      </div>
    </div>
    <div className="space-y-1.5 sm:col-span-2">
      <Label>Address</Label>
      <Textarea rows={2} value={values.address} onChange={(e) => onChange('address', e.target.value)} />
    </div>
    <div className="space-y-1.5 sm:col-span-2">
      <Label>Notes</Label>
      <Textarea rows={3} value={values.notes} onChange={(e) => onChange('notes', e.target.value)} />
    </div>
  </div>
);

const ApplicationRow = ({ application, srNo, onEdit, onDelete }) => (
  <TableRow>
    <TableCell className="text-zg-muted2">{srNo}</TableCell>
    <TableCell className="font-mono">{application.file_number || 'Pending'}</TableCell>
    <TableCell>{application.full_name}</TableCell>
    <TableCell className="font-mono">{application.passport_number}</TableCell>
    <TableCell>{application.nationality}</TableCell>
    <TableCell>{application.vendor || '—'}</TableCell>
    <TableCell>{application.vendor_country || '—'}</TableCell>
    <TableCell>{application.client || '—'}</TableCell>
    <TableCell>{application.client_country || '—'}</TableCell>
    <TableCell>{currentStageLabel(application)}</TableCell>
    <TableCell>{application.email || '—'}</TableCell>
    <TableCell>
      {application.phone_number ? `${application.phone_country_code || ''} ${application.phone_number}`.trim() : '—'}
    </TableCell>
    <TableCell>{application.address || '—'}</TableCell>
    <TableCell className="text-right space-x-2">
      <Button size="sm" variant="outline" onClick={() => onEdit(application)}>
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      <Button size="sm" variant="outline" onClick={() => onDelete(application)}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </TableCell>
  </TableRow>
);

const LeadRow = ({ lead, srNo, onView, onDelete, onStatusChange }) => (
  <TableRow>
    <TableCell className="text-zg-muted2">{srNo}</TableCell>
    <TableCell className="capitalize">{lead.partnership_type}</TableCell>
    <TableCell>{lead.full_name}</TableCell>
    <TableCell>{lead.company_name || '—'}</TableCell>
    <TableCell>{lead.email}</TableCell>
    <TableCell>{lead.phone}</TableCell>
    <TableCell>{lead.location || '—'}</TableCell>
    <TableCell>
      <Select value={lead.status} onValueChange={(value) => onStatusChange(lead, value)}>
        <SelectTrigger className="w-32 h-8 text-xs capitalize">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STATUS_OPTIONS.map((status) => (
            <SelectItem key={status} value={status} className="capitalize">
              {status}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </TableCell>
    <TableCell className="text-zg-muted2 whitespace-nowrap">{formatDate(lead.created_at)}</TableCell>
    <TableCell className="text-right space-x-2">
      <Button size="sm" variant="outline" onClick={() => onView(lead)}>
        <Eye className="h-3.5 w-3.5" />
      </Button>
      <Button size="sm" variant="outline" onClick={() => onDelete(lead)}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </TableCell>
  </TableRow>
);

const EntityRow = ({ entity, srNo, onEdit, onDelete }) => (
  <TableRow>
    <TableCell className="text-zg-muted2">{srNo}</TableCell>
    <TableCell>{entity.name}</TableCell>
    <TableCell>{entity.country || '—'}</TableCell>
    <TableCell>{entity.person_of_contact || '—'}</TableCell>
    <TableCell>{entity.email || '—'}</TableCell>
    <TableCell>{entity.phone_number || '—'}</TableCell>
    <TableCell className="text-right space-x-2">
      <Button size="sm" variant="outline" onClick={() => onEdit(entity)}>
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      <Button size="sm" variant="outline" onClick={() => onDelete(entity)}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </TableCell>
  </TableRow>
);

const VendorRow = ({ vendor, srNo, onEdit, onDelete, onViewLoginHistory, onSendCredentials }) => (
  <TableRow>
    <TableCell className="text-zg-muted2">{srNo}</TableCell>
    <TableCell>{vendor.name}</TableCell>
    <TableCell>{vendor.country || '—'}</TableCell>
    <TableCell>
      {vendor.username ? (
        <div>
          <p className="font-mono text-sm">{vendor.username}</p>
          <span className={`text-xs ${vendor.is_active ? 'text-emerald-600' : 'text-zg-muted2'}`}>
            {vendor.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      ) : (
        '—'
      )}
    </TableCell>
    <TableCell>
      {vendor.username ? (
        vendor.credentials_share_needed ? (
          <span className="inline-flex items-center gap-1 text-xs text-amber-600">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            Not sent
          </span>
        ) : vendor.credentials_shared_at ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
            <CheckCircle2 className="h-3 w-3" />
            Sent {formatDate(vendor.credentials_shared_at)}
          </span>
        ) : (
          <span className="text-xs text-zg-muted2">Reset password to share</span>
        )
      ) : (
        '—'
      )}
    </TableCell>
    <TableCell>{vendor.last_login_at ? formatDate(vendor.last_login_at) : 'Never'}</TableCell>
    <TableCell>{vendor.person_of_contact || '—'}</TableCell>
    <TableCell>{vendor.email || '—'}</TableCell>
    <TableCell>{vendor.phone_number || '—'}</TableCell>
    <TableCell className="text-right space-x-2 whitespace-nowrap">
      <Button size="sm" variant="outline" onClick={() => onViewLoginHistory(vendor)} disabled={!vendor.username}>
        <History className="h-3.5 w-3.5" />
      </Button>
      {vendor.credentials_share_needed && (
        <Button size="sm" variant="outline" onClick={() => onSendCredentials(vendor)} title="Send login credentials">
          <Mail className="h-3.5 w-3.5" />
        </Button>
      )}
      <Button size="sm" variant="outline" onClick={() => onEdit(vendor)}>
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      <Button size="sm" variant="outline" onClick={() => onDelete(vendor)}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </TableCell>
  </TableRow>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('applications'); // 'applications' | 'leads' | 'vendors' | 'clients' | 'vendor-queries'

  // --- Applications tab state ---
  const [applications, setApplications] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [groupBy, setGroupBy] = useState('none');
  const [pendingOnly, setPendingOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // --- Partnership leads tab state ---
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [leadDeleteTarget, setLeadDeleteTarget] = useState(null);
  const [viewingLead, setViewingLead] = useState(null);

  const [leadSortField, setLeadSortField] = useState(null);
  const [leadSortDirection, setLeadSortDirection] = useState('desc');
  const [leadGroupBy, setLeadGroupBy] = useState('none');
  const [leadCurrentPage, setLeadCurrentPage] = useState(1);

  // --- Vendors tab state ---
  const [vendorsLoading, setVendorsLoading] = useState(true);
  const [vendorFormOpen, setVendorFormOpen] = useState(false);
  const [vendorFormValues, setVendorFormValues] = useState(EMPTY_VENDOR_FORM);
  const [showVendorPassword, setShowVendorPassword] = useState(false);
  const [editingVendorId, setEditingVendorId] = useState(null);
  const [vendorSaving, setVendorSaving] = useState(false);
  const [vendorDeleteTarget, setVendorDeleteTarget] = useState(null);
  const [loginHistoryVendor, setLoginHistoryVendor] = useState(null);
  const [loginHistoryEvents, setLoginHistoryEvents] = useState([]);
  const [loginHistoryLoading, setLoginHistoryLoading] = useState(false);

  // --- Send Credentials state ---
  const [credentialsConfirmTarget, setCredentialsConfirmTarget] = useState(null);
  const [credentialsVendor, setCredentialsVendor] = useState(null);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [credentialsInfo, setCredentialsInfo] = useState(null);
  const [credentialsSubject, setCredentialsSubject] = useState('');
  const [credentialsMessageBody, setCredentialsMessageBody] = useState('');
  const [credentialsSending, setCredentialsSending] = useState(false);

  // --- Clients tab state ---
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientFormOpen, setClientFormOpen] = useState(false);
  const [clientFormValues, setClientFormValues] = useState(EMPTY_ENTITY_FORM);
  const [editingClientId, setEditingClientId] = useState(null);
  const [clientSaving, setClientSaving] = useState(false);
  const [clientDeleteTarget, setClientDeleteTarget] = useState(null);

  // --- Vendor's Queries tab state ---
  const [vendorQueries, setVendorQueries] = useState([]);
  const [vendorQueriesLoading, setVendorQueriesLoading] = useState(true);
  const [viewingVendorQuery, setViewingVendorQuery] = useState(null);
  const [vendorQueryDialogLoading, setVendorQueryDialogLoading] = useState(false);
  const [vendorQueryResponseDraft, setVendorQueryResponseDraft] = useState('');
  const [vendorQuerySaving, setVendorQuerySaving] = useState(false);

  const goToLogin = useCallback(() => {
    navigate(`/${ADMIN_BASE_PATH}`);
  }, [navigate]);

  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminFetch('/applications');
      if (!res.ok) {
        toast.error('Failed to load applications.');
        return;
      }
      setApplications(await res.json());
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to load applications.');
    } finally {
      setLoading(false);
    }
  }, [goToLogin]);

  const loadVendors = useCallback(async () => {
    setVendorsLoading(true);
    try {
      const res = await adminFetch('/vendors');
      if (!res.ok) {
        toast.error('Failed to load vendors.');
        return;
      }
      setVendors(await res.json());
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to load vendors.');
    } finally {
      setVendorsLoading(false);
    }
  }, [goToLogin]);

  const loadClients = useCallback(async () => {
    setClientsLoading(true);
    try {
      const res = await adminFetch('/clients');
      if (!res.ok) {
        toast.error('Failed to load clients.');
        return;
      }
      setClients(await res.json());
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to load clients.');
    } finally {
      setClientsLoading(false);
    }
  }, [goToLogin]);

  const loadLeads = useCallback(async () => {
    setLeadsLoading(true);
    try {
      const res = await adminFetch('/partnership-applications');
      if (!res.ok) {
        toast.error('Failed to load partnership leads.');
        return;
      }
      setLeads(await res.json());
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to load partnership leads.');
    } finally {
      setLeadsLoading(false);
    }
  }, [goToLogin]);

  const loadVendorQueries = useCallback(async () => {
    setVendorQueriesLoading(true);
    try {
      const res = await adminFetch('/vendor-queries');
      if (!res.ok) {
        toast.error('Failed to load vendor queries.');
        return;
      }
      setVendorQueries(await res.json());
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to load vendor queries.');
    } finally {
      setVendorQueriesLoading(false);
    }
  }, [goToLogin]);

  useEffect(() => {
    if (!sessionStorage.getItem(ADMIN_TOKEN_KEY)) {
      goToLogin();
      return;
    }
    loadApplications();
    loadVendors();
    loadClients();
    loadLeads();
    loadVendorQueries();
  }, [goToLogin, loadApplications, loadVendors, loadClients, loadLeads, loadVendorQueries]);

  const openVendorQuery = async (query) => {
    setVendorQueryDialogLoading(true);
    try {
      const res = await adminFetch(`/vendor-queries/${query.id}`);
      if (!res.ok) {
        toast.error('Failed to load query details.');
        return;
      }
      const detail = await res.json();
      setViewingVendorQuery(detail);
      setVendorQueryResponseDraft(detail.admin_response || '');
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to load query details.');
    } finally {
      setVendorQueryDialogLoading(false);
    }
  };

  const handleSaveVendorQueryDraft = async () => {
    if (!viewingVendorQuery) return;
    setVendorQuerySaving(true);
    try {
      const res = await adminFetch(`/vendor-queries/${viewingVendorQuery.id}/draft`, {
        method: 'PATCH',
        body: JSON.stringify({ admin_response: vendorQueryResponseDraft }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        toast.error(body?.detail || 'Failed to save draft.');
        return;
      }
      toast.success('Draft saved.');
      setViewingVendorQuery(await res.json());
      loadVendorQueries();
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to save draft.');
    } finally {
      setVendorQuerySaving(false);
    }
  };

  const handleSendVendorQueryResponse = async () => {
    if (!viewingVendorQuery) return;
    setVendorQuerySaving(true);
    try {
      const res = await adminFetch(`/vendor-queries/${viewingVendorQuery.id}/send-response`, {
        method: 'POST',
        body: JSON.stringify({ admin_response: vendorQueryResponseDraft }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        toast.error(body?.detail || 'Failed to send response.');
        return;
      }
      toast.success('Response sent and query closed.');
      setViewingVendorQuery(null);
      loadVendorQueries();
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to send response.');
    } finally {
      setVendorQuerySaving(false);
    }
  };

  const handleResolveVendorQueryManually = async () => {
    if (!viewingVendorQuery) return;
    setVendorQuerySaving(true);
    try {
      const res = await adminFetch(`/vendor-queries/${viewingVendorQuery.id}/resolve-manually`, {
        method: 'POST',
        body: JSON.stringify({ admin_response: vendorQueryResponseDraft || null }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        toast.error(body?.detail || 'Failed to resolve query.');
        return;
      }
      toast.success('Query marked as resolved.');
      setViewingVendorQuery(null);
      loadVendorQueries();
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to resolve query.');
    } finally {
      setVendorQuerySaving(false);
    }
  };

  // --- Applications: filter / sort / group / paginate ---
  const visibleApplications = useMemo(
    () => (pendingOnly ? applications.filter((a) => !a.file_number) : applications),
    [applications, pendingOnly],
  );

  const sortedApplications = useMemo(() => {
    if (!sortField) return visibleApplications;
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    return [...visibleApplications].sort((a, b) => {
      const result = collator.compare(a[sortField] || '', b[sortField] || '');
      return sortDirection === 'asc' ? result : -result;
    });
  }, [visibleApplications, sortField, sortDirection]);

  // Serial number reflects each row's position in the current sort order,
  // and stays stable whether the table is paginated or grouped.
  const srNoById = useMemo(() => {
    const map = new Map();
    sortedApplications.forEach((application, index) => map.set(application.id, index + 1));
    return map;
  }, [sortedApplications]);

  const totalPages = Math.max(1, Math.ceil(sortedApplications.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);

  const paginatedApplications = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return sortedApplications.slice(start, start + PAGE_SIZE);
  }, [sortedApplications, safePage]);

  const groupedApplications = useMemo(() => {
    if (groupBy === 'none') return null;
    const groups = new Map();
    sortedApplications.forEach((application) => {
      const value = application[groupBy] || UNASSIGNED_GROUP_LABEL;
      if (!groups.has(value)) groups.set(value, []);
      groups.get(value).push(application);
    });
    return [...groups.entries()].sort(([a], [b]) => {
      if (a === UNASSIGNED_GROUP_LABEL) return 1;
      if (b === UNASSIGNED_GROUP_LABEL) return -1;
      return a.localeCompare(b);
    });
  }, [sortedApplications, groupBy]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const handleGroupByChange = (value) => {
    setGroupBy(value);
    setCurrentPage(1);
  };

  const handlePendingOnlyChange = (checked) => {
    setPendingOnly(checked);
    setCurrentPage(1);
  };

  const sortIcon = (field, currentField, direction) => {
    if (currentField !== field) return <ArrowUpDown className="h-3.5 w-3.5 text-zg-muted2" />;
    return direction === 'asc' ? (
      <ArrowUp className="h-3.5 w-3.5 text-zg-ink" />
    ) : (
      <ArrowDown className="h-3.5 w-3.5 text-zg-ink" />
    );
  };

  // --- Leads: sort / group / paginate ---
  const sortedLeads = useMemo(() => {
    if (!leadSortField) return leads;
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    return [...leads].sort((a, b) => {
      const result = collator.compare(String(a[leadSortField] || ''), String(b[leadSortField] || ''));
      return leadSortDirection === 'asc' ? result : -result;
    });
  }, [leads, leadSortField, leadSortDirection]);

  const leadSrNoById = useMemo(() => {
    const map = new Map();
    sortedLeads.forEach((lead, index) => map.set(lead.id, index + 1));
    return map;
  }, [sortedLeads]);

  const leadTotalPages = Math.max(1, Math.ceil(sortedLeads.length / PAGE_SIZE));
  const leadSafePage = Math.min(leadCurrentPage, leadTotalPages);

  const paginatedLeads = useMemo(() => {
    const start = (leadSafePage - 1) * PAGE_SIZE;
    return sortedLeads.slice(start, start + PAGE_SIZE);
  }, [sortedLeads, leadSafePage]);

  const groupedLeads = useMemo(() => {
    if (leadGroupBy === 'none') return null;
    const groups = new Map();
    sortedLeads.forEach((lead) => {
      const value = lead[leadGroupBy] || UNASSIGNED_GROUP_LABEL;
      if (!groups.has(value)) groups.set(value, []);
      groups.get(value).push(lead);
    });
    return [...groups.entries()].sort(([a], [b]) => {
      if (a === UNASSIGNED_GROUP_LABEL) return 1;
      if (b === UNASSIGNED_GROUP_LABEL) return -1;
      return a.localeCompare(b);
    });
  }, [sortedLeads, leadGroupBy]);

  const handleLeadSort = (field) => {
    if (leadSortField === field) {
      setLeadSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'));
    } else {
      setLeadSortField(field);
      setLeadSortDirection('asc');
    }
    setLeadCurrentPage(1);
  };

  const handleLeadGroupByChange = (value) => {
    setLeadGroupBy(value);
    setLeadCurrentPage(1);
  };

  const handleLeadStatusChange = async (lead, status) => {
    try {
      const res = await adminFetch(`/partnership-applications/${lead.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        toast.error('Failed to update status.');
        return;
      }
      toast.success('Status updated.');
      loadLeads();
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to update status.');
    }
  };

  const handleLeadDelete = async () => {
    if (!leadDeleteTarget) return;
    try {
      const res = await adminFetch(`/partnership-applications/${leadDeleteTarget.id}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) {
        toast.error('Failed to delete lead.');
        return;
      }
      toast.success('Lead deleted.');
      setLeadDeleteTarget(null);
      loadLeads();
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to delete lead.');
    }
  };

  // --- Applications form handlers ---
  const openAddForm = () => {
    setEditingId(null);
    setFormValues(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEditForm = (application) => {
    setEditingId(application.id);
    setFormValues({
      file_number: application.file_number || '',
      passport_number: application.passport_number,
      full_name: application.full_name,
      nationality: application.nationality,
      vendor: application.vendor || '',
      client: application.client || '',
      notes: application.notes || '',
      email: application.email || '',
      phone_country_code: application.phone_country_code || '',
      phone_number: application.phone_number || '',
      address: application.address || '',
      ...Object.fromEntries(STAGE_FIELDS.map(([key]) => [key, application[key] || ''])),
    });
    setFormOpen(true);
  };

  const handleFieldChange = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!formValues.nationality.trim()) {
      toast.error('Nationality is required.');
      return;
    }

    setSaving(true);

    const payload = {
      ...formValues,
      file_number: formValues.file_number || null,
      vendor: formValues.vendor || null,
      client: formValues.client || null,
      notes: formValues.notes || null,
      email: formValues.email || null,
      phone_country_code: formValues.phone_country_code || null,
      phone_number: formValues.phone_number || null,
      address: formValues.address || null,
      ...Object.fromEntries(STAGE_FIELDS.map(([key]) => [key, formValues[key] || null])),
    };

    try {
      const res = editingId
        ? await adminFetch(`/applications/${editingId}`, {
            method: 'PATCH',
            body: JSON.stringify(payload),
          })
        : await adminFetch('/applications', {
            method: 'POST',
            body: JSON.stringify(payload),
          });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast.error(body.detail || 'Failed to save candidate.');
        return;
      }

      toast.success(editingId ? 'Candidate updated.' : 'Candidate added.');
      setFormOpen(false);
      loadApplications();
      loadVendors();
      loadClients();
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to save candidate.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await adminFetch(`/applications/${deleteTarget.id}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) {
        toast.error('Failed to delete candidate.');
        return;
      }
      toast.success('Candidate deleted.');
      setDeleteTarget(null);
      loadApplications();
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to delete candidate.');
    }
  };

  // --- Vendors tab handlers ---
  const openVendorAddForm = () => {
    setEditingVendorId(null);
    setVendorFormValues(EMPTY_VENDOR_FORM);
    setShowVendorPassword(false);
    setVendorFormOpen(true);
  };

  const openVendorEditForm = (vendor) => {
    setEditingVendorId(vendor.id);
    setVendorFormValues({
      name: vendor.name,
      country: vendor.country || '',
      username: vendor.username || '',
      password: '',
      is_active: vendor.is_active,
      person_of_contact: vendor.person_of_contact || '',
      email: vendor.email || '',
      phone_number: vendor.phone_number || '',
    });
    setShowVendorPassword(false);
    setVendorFormOpen(true);
  };

  const handleVendorFieldChange = (key, value) => {
    setVendorFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleVendorFormSubmit = async (e) => {
    e.preventDefault();

    if (!vendorFormValues.name.trim()) {
      toast.error('Name is required.');
      return;
    }

    // A vendor login requires both a username and a password - only checked
    // here when there's no password on file yet (i.e. this username has
    // never been usable to log in), since PATCHing just one of the two on
    // an already-credentialed vendor is fine (e.g. resetting only the
    // password, or renaming only the username).
    const isNewCredentials = !editingVendorId || !vendorFormValues.username;
    if (isNewCredentials && (vendorFormValues.username.trim() || vendorFormValues.password)) {
      if (!vendorFormValues.username.trim() || !vendorFormValues.password) {
        toast.error('A vendor login requires both a username and a password.');
        return;
      }
    }

    setVendorSaving(true);

    const payload = {
      name: vendorFormValues.name,
      country: vendorFormValues.country || null,
      username: vendorFormValues.username.trim() || null,
      is_active: vendorFormValues.is_active,
      person_of_contact: vendorFormValues.person_of_contact || null,
      email: vendorFormValues.email || null,
      phone_number: vendorFormValues.phone_number || null,
      ...(vendorFormValues.password ? { password: vendorFormValues.password } : {}),
    };

    try {
      const res = editingVendorId
        ? await adminFetch(`/vendors/${editingVendorId}`, { method: 'PATCH', body: JSON.stringify(payload) })
        : await adminFetch('/vendors', { method: 'POST', body: JSON.stringify(payload) });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast.error(body.detail || 'Failed to save vendor.');
        return;
      }

      toast.success(editingVendorId ? 'Vendor updated.' : 'Vendor added.');
      setVendorFormOpen(false);
      loadVendors();
      loadApplications();
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to save vendor.');
    } finally {
      setVendorSaving(false);
    }
  };

  const handleVendorDelete = async () => {
    if (!vendorDeleteTarget) return;
    try {
      const res = await adminFetch(`/vendors/${vendorDeleteTarget.id}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) {
        toast.error('Failed to delete vendor.');
        return;
      }
      toast.success('Vendor deleted.');
      setVendorDeleteTarget(null);
      loadVendors();
      loadApplications();
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to delete vendor.');
    }
  };

  const openVendorLoginHistory = async (vendor) => {
    setLoginHistoryVendor(vendor);
    setLoginHistoryLoading(true);
    try {
      const res = await adminFetch(`/vendors/${vendor.id}/login-events`);
      if (!res.ok) {
        toast.error('Failed to load login history.');
        return;
      }
      setLoginHistoryEvents(await res.json());
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to load login history.');
    } finally {
      setLoginHistoryLoading(false);
    }
  };

  // --- Send Credentials handlers ---
  const confirmSendCredentials = (vendor) => {
    setCredentialsConfirmTarget(vendor);
  };

  const buildCredentialsMessage = (info) => {
    const loginUrl = `${window.location.origin}${info.login_path}`;
    return (
      `Hello ${info.vendor_name},\n\n` +
      `Here are your login credentials for the Zeevogo Vendor Portal:\n\n` +
      `Username: ${info.username}\n` +
      `Password: ${info.password}\n\n` +
      `Login here: ${loginUrl}\n\n` +
      `Regards,\nZeevogo Team`
    );
  };

  const proceedToComposeCredentials = async () => {
    const vendor = credentialsConfirmTarget;
    setCredentialsConfirmTarget(null);
    if (!vendor) return;
    setCredentialsVendor(vendor);
    setCredentialsLoading(true);
    try {
      const res = await adminFetch(`/vendors/${vendor.id}/credentials-message`);
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        toast.error(body?.detail || 'Failed to load credentials.');
        setCredentialsVendor(null);
        return;
      }
      const info = await res.json();
      setCredentialsInfo(info);
      setCredentialsSubject('Your Zeevogo Vendor Portal Login');
      setCredentialsMessageBody(buildCredentialsMessage(info));
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to load credentials.');
      setCredentialsVendor(null);
    } finally {
      setCredentialsLoading(false);
    }
  };

  const closeCredentialsDialog = () => {
    setCredentialsVendor(null);
    setCredentialsInfo(null);
    setCredentialsSubject('');
    setCredentialsMessageBody('');
  };

  const handleSendCredentialsEmail = async () => {
    if (!credentialsVendor) return;
    setCredentialsSending(true);
    try {
      const res = await adminFetch(`/vendors/${credentialsVendor.id}/send-credentials-email`, {
        method: 'POST',
        body: JSON.stringify({ subject: credentialsSubject, message: credentialsMessageBody }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        toast.error(body?.detail || 'Failed to send credentials.');
        return;
      }
      toast.success('Credentials emailed to the vendor.');
      closeCredentialsDialog();
      loadVendors();
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to send credentials.');
    } finally {
      setCredentialsSending(false);
    }
  };

  const handleMarkCredentialsSharedManually = async () => {
    if (!credentialsVendor) return;
    setCredentialsSending(true);
    try {
      const res = await adminFetch(
        `/vendors/${credentialsVendor.id}/mark-credentials-shared-manually`,
        { method: 'POST' },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        toast.error(body?.detail || 'Failed to record this as shared.');
        return;
      }
      toast.success('Marked as shared.');
      closeCredentialsDialog();
      loadVendors();
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to record this as shared.');
    } finally {
      setCredentialsSending(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard.`);
    } catch {
      toast.error(`Failed to copy ${label.toLowerCase()}.`);
    }
  };

  // --- Clients tab handlers ---
  const openClientAddForm = () => {
    setEditingClientId(null);
    setClientFormValues(EMPTY_ENTITY_FORM);
    setClientFormOpen(true);
  };

  const openClientEditForm = (client) => {
    setEditingClientId(client.id);
    setClientFormValues({
      name: client.name,
      country: client.country || '',
      person_of_contact: client.person_of_contact || '',
      email: client.email || '',
      phone_number: client.phone_number || '',
    });
    setClientFormOpen(true);
  };

  const handleClientFieldChange = (key, value) => {
    setClientFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleClientFormSubmit = async (e) => {
    e.preventDefault();

    if (!clientFormValues.name.trim()) {
      toast.error('Name is required.');
      return;
    }

    setClientSaving(true);

    const payload = {
      name: clientFormValues.name,
      country: clientFormValues.country || null,
      person_of_contact: clientFormValues.person_of_contact || null,
      email: clientFormValues.email || null,
      phone_number: clientFormValues.phone_number || null,
    };

    try {
      const res = editingClientId
        ? await adminFetch(`/clients/${editingClientId}`, { method: 'PATCH', body: JSON.stringify(payload) })
        : await adminFetch('/clients', { method: 'POST', body: JSON.stringify(payload) });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast.error(body.detail || 'Failed to save client.');
        return;
      }

      toast.success(editingClientId ? 'Client updated.' : 'Client added.');
      setClientFormOpen(false);
      loadClients();
      loadApplications();
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to save client.');
    } finally {
      setClientSaving(false);
    }
  };

  const handleClientDelete = async () => {
    if (!clientDeleteTarget) return;
    try {
      const res = await adminFetch(`/clients/${clientDeleteTarget.id}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) {
        toast.error('Failed to delete client.');
        return;
      }
      toast.success('Client deleted.');
      setClientDeleteTarget(null);
      loadClients();
      loadApplications();
    } catch (err) {
      if (err instanceof AdminAuthError) goToLogin();
      else toast.error('Failed to delete client.');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    goToLogin();
  };

  const handleExportAll = () => {
    exportWorkbookToExcel('zeevogo-applications', [
      {
        name: 'Applications',
        rows: applications.map((a) => ({
          'File No.': a.file_number,
          Name: a.full_name,
          'Passport No.': a.passport_number,
          Nationality: a.nationality,
          Vendor: a.vendor || '',
          'Vendor Country': a.vendor_country || '',
          Client: a.client || '',
          'Client Country': a.client_country || '',
          ...Object.fromEntries(STAGE_FIELDS.map(([key, label]) => [label, formatDate(a[key])])),
          Email: a.email || '',
          Phone: a.phone_number ? `${a.phone_country_code || ''} ${a.phone_number}`.trim() : '',
          Address: a.address || '',
          Notes: a.notes || '',
        })),
      },
      {
        name: 'Partnership Leads',
        rows: leads.map((l) => ({
          Type: l.partnership_type,
          Name: l.full_name,
          Company: l.company_name || '',
          'Company Size': l.company_size || '',
          Availability: l.availability || '',
          Email: l.email,
          Phone: l.phone,
          Location: l.location || '',
          Experience: l.experience || '',
          Message: l.message || '',
          Status: l.status,
          Submitted: formatDate(l.created_at),
        })),
      },
      {
        name: 'Vendors',
        rows: vendors.map((v) => ({
          Name: v.name,
          Country: v.country || '',
          Username: v.username || '',
          Status: v.username ? (v.is_active ? 'Active' : 'Inactive') : '',
          'Last Login': v.last_login_at ? formatDate(v.last_login_at) : 'Never',
          'Person of Contact': v.person_of_contact || '',
          Email: v.email || '',
          'Phone Number': v.phone_number || '',
        })),
      },
      {
        name: 'Clients',
        rows: clients.map((c) => ({
          Name: c.name,
          Country: c.country || '',
          'Person of Contact': c.person_of_contact || '',
          Email: c.email || '',
          'Phone Number': c.phone_number || '',
        })),
      },
    ]);
  };

  const columnCount = 14;
  const leadColumnCount = 10;
  const entityColumnCount = 7;
  const vendorColumnCount = 10;

  return (
    <div className="min-h-screen bg-zg-surface">
      <div className="border-b border-zg-border bg-zg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo markSize={30} wordmarkSize={22} />
            <span className="hidden sm:block h-5 w-px" style={{ background: 'var(--zg-border2)' }} />
            <span className="hidden sm:inline text-sm font-semibold" style={{ color: 'var(--zg-muted)' }}>
              Zeevogo Admin Portal
            </span>
          </div>
          <Button variant="outline" size="icon" onClick={toggleTheme} title="Toggle theme">
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <div className="py-10 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setActiveTab('applications')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'applications' ? 'bg-zg-espresso text-white' : 'text-zg-muted hover:bg-zg-surface2'
                }`}
              >
                Applications
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('leads')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'leads' ? 'bg-zg-espresso text-white' : 'text-zg-muted hover:bg-zg-surface2'
                }`}
              >
                Partnership Leads
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('vendors')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'vendors' ? 'bg-zg-espresso text-white' : 'text-zg-muted hover:bg-zg-surface2'
                }`}
              >
                Vendors
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('clients')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'clients' ? 'bg-zg-espresso text-white' : 'text-zg-muted hover:bg-zg-surface2'
                }`}
              >
                Clients
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('vendor-queries')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'vendor-queries' ? 'bg-zg-espresso text-white' : 'text-zg-muted hover:bg-zg-surface2'
                }`}
              >
                Vendor's Queries
              </button>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={handleExportAll}>
                <FileDown className="h-4 w-4 mr-2" />
                Export to Excel
              </Button>
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>

          {activeTab === 'applications' && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <h1 className="text-2xl font-bold text-zg-ink">Applications</h1>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-zg-muted whitespace-nowrap">Group by</Label>
                    <Select value={groupBy} onValueChange={handleGroupByChange}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {GROUPABLE_COLUMNS.map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch id="pending-only" checked={pendingOnly} onCheckedChange={handlePendingOnlyChange} />
                    <Label htmlFor="pending-only" className="text-sm text-zg-muted whitespace-nowrap cursor-pointer">
                      Pending file number only
                    </Label>
                  </div>
                  <Button onClick={openAddForm}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Candidate
                  </Button>
                </div>
              </div>

              <Card className="border-zg-border rounded-none">
                <CardContent className="p-0">
                  {loading ? (
                    <div className="flex items-center justify-center py-16">
                      <Loader2 className="h-6 w-6 text-zg-muted animate-spin" />
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">Sr No.</TableHead>
                          {SORTABLE_COLUMNS.map(([field, label]) => (
                            <TableHead key={field}>
                              <button
                                type="button"
                                onClick={() => handleSort(field)}
                                className="flex items-center gap-1.5 font-medium hover:text-zg-ink"
                              >
                                {label}
                                {sortIcon(field, sortField, sortDirection)}
                              </button>
                            </TableHead>
                          ))}
                          <TableHead>Nationality</TableHead>
                          <TableHead>Vendor</TableHead>
                          <TableHead>Vendor Country</TableHead>
                          <TableHead>Client</TableHead>
                          <TableHead>Client Country</TableHead>
                          <TableHead>Current Stage</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>Address</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {groupedApplications
                          ? groupedApplications.map(([groupValue, items]) => (
                              <React.Fragment key={groupValue}>
                                <TableRow className="bg-zg-surface hover:bg-zg-surface">
                                  <TableCell colSpan={columnCount} className="font-semibold text-zg-ink">
                                    {groupValue} <span className="text-zg-muted2 font-normal">({items.length})</span>
                                  </TableCell>
                                </TableRow>
                                {items.map((application) => (
                                  <ApplicationRow
                                    key={application.id}
                                    application={application}
                                    srNo={srNoById.get(application.id)}
                                    onEdit={openEditForm}
                                    onDelete={setDeleteTarget}
                                  />
                                ))}
                              </React.Fragment>
                            ))
                          : paginatedApplications.map((application) => (
                              <ApplicationRow
                                key={application.id}
                                application={application}
                                srNo={srNoById.get(application.id)}
                                onEdit={openEditForm}
                                onDelete={setDeleteTarget}
                              />
                            ))}
                        {applications.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={columnCount} className="text-center py-10 text-zg-muted">
                              No candidates yet.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {!loading && !groupedApplications && sortedApplications.length > 0 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-zg-muted2">
                    Page {safePage} of {totalPages} &middot; {sortedApplications.length} total
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={safePage <= 1}
                      onClick={() => setCurrentPage(safePage - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={safePage >= totalPages}
                      onClick={() => setCurrentPage(safePage + 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {activeTab === 'leads' && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <h1 className="text-2xl font-bold text-zg-ink">Partnership Leads</h1>
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-zg-muted whitespace-nowrap">Group by</Label>
                  <Select value={leadGroupBy} onValueChange={handleLeadGroupByChange}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_GROUPABLE_COLUMNS.map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Card className="border-zg-border rounded-none">
                <CardContent className="p-0">
                  {leadsLoading ? (
                    <div className="flex items-center justify-center py-16">
                      <Loader2 className="h-6 w-6 text-zg-muted animate-spin" />
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">Sr No.</TableHead>
                          <TableHead>Type</TableHead>
                          {LEAD_SORTABLE_COLUMNS.slice(0, 1).map(([field, label]) => (
                            <TableHead key={field}>
                              <button
                                type="button"
                                onClick={() => handleLeadSort(field)}
                                className="flex items-center gap-1.5 font-medium hover:text-zg-ink"
                              >
                                {label}
                                {sortIcon(field, leadSortField, leadSortDirection)}
                              </button>
                            </TableHead>
                          ))}
                          <TableHead>Company</TableHead>
                          {LEAD_SORTABLE_COLUMNS.slice(1, 2).map(([field, label]) => (
                            <TableHead key={field}>
                              <button
                                type="button"
                                onClick={() => handleLeadSort(field)}
                                className="flex items-center gap-1.5 font-medium hover:text-zg-ink"
                              >
                                {label}
                                {sortIcon(field, leadSortField, leadSortDirection)}
                              </button>
                            </TableHead>
                          ))}
                          <TableHead>Phone</TableHead>
                          <TableHead>Location</TableHead>
                          <TableHead>Status</TableHead>
                          {LEAD_SORTABLE_COLUMNS.slice(2, 3).map(([field, label]) => (
                            <TableHead key={field}>
                              <button
                                type="button"
                                onClick={() => handleLeadSort(field)}
                                className="flex items-center gap-1.5 font-medium hover:text-zg-ink"
                              >
                                {label}
                                {sortIcon(field, leadSortField, leadSortDirection)}
                              </button>
                            </TableHead>
                          ))}
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {groupedLeads
                          ? groupedLeads.map(([groupValue, items]) => (
                              <React.Fragment key={groupValue}>
                                <TableRow className="bg-zg-surface hover:bg-zg-surface">
                                  <TableCell colSpan={leadColumnCount} className="font-semibold text-zg-ink capitalize">
                                    {groupValue} <span className="text-zg-muted2 font-normal">({items.length})</span>
                                  </TableCell>
                                </TableRow>
                                {items.map((lead) => (
                                  <LeadRow
                                    key={lead.id}
                                    lead={lead}
                                    srNo={leadSrNoById.get(lead.id)}
                                    onView={setViewingLead}
                                    onDelete={setLeadDeleteTarget}
                                    onStatusChange={handleLeadStatusChange}
                                  />
                                ))}
                              </React.Fragment>
                            ))
                          : paginatedLeads.map((lead) => (
                              <LeadRow
                                key={lead.id}
                                lead={lead}
                                srNo={leadSrNoById.get(lead.id)}
                                onView={setViewingLead}
                                onDelete={setLeadDeleteTarget}
                                onStatusChange={handleLeadStatusChange}
                              />
                            ))}
                        {leads.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={leadColumnCount} className="text-center py-10 text-zg-muted">
                              No partnership leads yet.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {!leadsLoading && !groupedLeads && sortedLeads.length > 0 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-zg-muted2">
                    Page {leadSafePage} of {leadTotalPages} &middot; {sortedLeads.length} total
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={leadSafePage <= 1}
                      onClick={() => setLeadCurrentPage(leadSafePage - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={leadSafePage >= leadTotalPages}
                      onClick={() => setLeadCurrentPage(leadSafePage + 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {activeTab === 'vendors' && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <h1 className="text-2xl font-bold text-zg-ink">Vendors</h1>
                <Button onClick={openVendorAddForm}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Vendor
                </Button>
              </div>

              <Card className="border-zg-border rounded-none">
                <CardContent className="p-0">
                  {vendorsLoading ? (
                    <div className="flex items-center justify-center py-16">
                      <Loader2 className="h-6 w-6 text-zg-muted animate-spin" />
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">Sr No.</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Country</TableHead>
                          <TableHead>Portal Access</TableHead>
                          <TableHead>Credentials</TableHead>
                          <TableHead>Last Login</TableHead>
                          <TableHead>Person of Contact</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Phone Number</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {vendors.map((vendor, index) => (
                          <VendorRow
                            key={vendor.id}
                            vendor={vendor}
                            srNo={index + 1}
                            onEdit={openVendorEditForm}
                            onDelete={setVendorDeleteTarget}
                            onViewLoginHistory={openVendorLoginHistory}
                            onSendCredentials={confirmSendCredentials}
                          />
                        ))}
                        {vendors.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={vendorColumnCount} className="text-center py-10 text-zg-muted">
                              No vendors yet.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {activeTab === 'clients' && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <h1 className="text-2xl font-bold text-zg-ink">Clients</h1>
                <Button onClick={openClientAddForm}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Client
                </Button>
              </div>

              <Card className="border-zg-border rounded-none">
                <CardContent className="p-0">
                  {clientsLoading ? (
                    <div className="flex items-center justify-center py-16">
                      <Loader2 className="h-6 w-6 text-zg-muted animate-spin" />
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">Sr No.</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Country</TableHead>
                          <TableHead>Person of Contact</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Phone Number</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {clients.map((client, index) => (
                          <EntityRow
                            key={client.id}
                            entity={client}
                            srNo={index + 1}
                            onEdit={openClientEditForm}
                            onDelete={setClientDeleteTarget}
                          />
                        ))}
                        {clients.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={entityColumnCount} className="text-center py-10 text-zg-muted">
                              No clients yet.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {activeTab === 'vendor-queries' && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <h1 className="text-2xl font-bold text-zg-ink">Vendor's Queries</h1>
              </div>

              <Card className="border-zg-border rounded-none">
                <CardContent className="p-0">
                  {vendorQueriesLoading ? (
                    <div className="flex items-center justify-center py-16">
                      <Loader2 className="h-6 w-6 text-zg-muted animate-spin" />
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">Sr No.</TableHead>
                          <TableHead>Vendor Name</TableHead>
                          <TableHead>Subject</TableHead>
                          <TableHead>Query Created Date</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {vendorQueries.map((query, index) => (
                          <TableRow key={query.id}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{query.vendor_name}</TableCell>
                            <TableCell>{query.subject}</TableCell>
                            <TableCell>{new Date(query.created_at).toLocaleString()}</TableCell>
                            <TableCell className="text-right">
                              {query.resolved ? (
                                <Button variant="outline" size="sm" onClick={() => openVendorQuery(query)}>
                                  <Eye className="h-3.5 w-3.5 mr-1" />
                                  View Query
                                </Button>
                              ) : (
                                <Button size="sm" onClick={() => openVendorQuery(query)}>
                                  Send Response
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        {vendorQueries.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-10 text-zg-muted">
                              No vendor queries yet.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>

      {/* View / respond to a vendor query */}
      <Dialog open={!!viewingVendorQuery} onOpenChange={(open) => !open && setViewingVendorQuery(null)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto rounded-[18px]">
          <DialogHeader>
            <DialogTitle>{viewingVendorQuery?.subject}</DialogTitle>
          </DialogHeader>
          {vendorQueryDialogLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 text-zg-muted animate-spin" />
            </div>
          ) : (
            viewingVendorQuery && (
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-4 pb-4 border-b border-zg-border">
                  <div>
                    <p className="text-zg-muted2 text-xs uppercase tracking-wide">Vendor</p>
                    <p className="text-zg-ink">{viewingVendorQuery.vendor_name}</p>
                  </div>
                  <div>
                    <p className="text-zg-muted2 text-xs uppercase tracking-wide">Submitted</p>
                    <p className="text-zg-ink">{new Date(viewingVendorQuery.created_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-zg-muted2 text-xs uppercase tracking-wide">Candidate Name</p>
                    <p className="text-zg-ink">{viewingVendorQuery.candidate_full_name}</p>
                  </div>
                  <div>
                    <p className="text-zg-muted2 text-xs uppercase tracking-wide">Candidate File No.</p>
                    <p className="font-mono text-zg-ink">{viewingVendorQuery.candidate_file_number}</p>
                  </div>
                  <div>
                    <p className="text-zg-muted2 text-xs uppercase tracking-wide">Candidate Passport ID</p>
                    <p className="font-mono text-zg-ink">{viewingVendorQuery.candidate_passport_number}</p>
                  </div>
                </div>
                <div>
                  <p className="text-zg-muted2 text-xs uppercase tracking-wide mb-1">Vendor's Message</p>
                  <p className="text-zg-ink rounded-lg bg-zg-surface2 p-3">{viewingVendorQuery.message}</p>
                </div>

                {viewingVendorQuery.resolved ? (
                  <div>
                    <p className="text-zg-muted2 text-xs uppercase tracking-wide mb-1">Admin's Response</p>
                    <p className="text-zg-ink rounded-lg bg-zg-surface2 p-3">
                      {viewingVendorQuery.admin_response || '(Resolved without a written response)'}
                    </p>
                    {viewingVendorQuery.resolved_date && (
                      <p className="text-zg-muted2 text-xs mt-2">
                        Resolved {new Date(viewingVendorQuery.resolved_date).toLocaleString()}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Admin's Message</Label>
                    <Textarea
                      value={vendorQueryResponseDraft}
                      onChange={(e) => setVendorQueryResponseDraft(e.target.value)}
                      rows={5}
                    />
                  </div>
                )}
              </div>
            )
          )}
          {viewingVendorQuery && !viewingVendorQuery.resolved && (
            <DialogFooter className="flex-wrap gap-2">
              <Button variant="outline" onClick={handleSaveVendorQueryDraft} disabled={vendorQuerySaving}>
                Save
              </Button>
              {viewingVendorQuery.vendor_has_valid_email ? (
                <Button onClick={handleSendVendorQueryResponse} disabled={vendorQuerySaving}>
                  Send response and close
                </Button>
              ) : (
                <Button onClick={handleResolveVendorQueryManually} disabled={vendorQuerySaving}>
                  Manually resolved and Close
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Add / Edit application form */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-[18px]">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Candidate' : 'Add Candidate'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleFormSubmit} className="space-y-6">
            <ApplicationFormFields
              values={formValues}
              onChange={handleFieldChange}
              vendorOptions={vendors.map((v) => v.name)}
              clientOptions={clients.map((c) => c.name)}
            />
            <DialogFooter>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete application confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-[18px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this candidate?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove {deleteTarget?.full_name} ({deleteTarget?.file_number}) from the
              tracking table. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View lead details */}
      <Dialog open={!!viewingLead} onOpenChange={(open) => !open && setViewingLead(null)}>
        <DialogContent className="max-w-lg rounded-[18px]">
          <DialogHeader>
            <DialogTitle className="capitalize">{viewingLead?.partnership_type} Lead</DialogTitle>
          </DialogHeader>
          {viewingLead && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-zg-muted2 text-xs uppercase tracking-wide">Name</p>
                  <p className="text-zg-ink">{viewingLead.full_name}</p>
                </div>
                {viewingLead.company_name && (
                  <div>
                    <p className="text-zg-muted2 text-xs uppercase tracking-wide">Company</p>
                    <p className="text-zg-ink">{viewingLead.company_name}</p>
                  </div>
                )}
                {viewingLead.company_size && (
                  <div>
                    <p className="text-zg-muted2 text-xs uppercase tracking-wide">Company Size</p>
                    <p className="text-zg-ink">{viewingLead.company_size}</p>
                  </div>
                )}
                {viewingLead.availability && (
                  <div>
                    <p className="text-zg-muted2 text-xs uppercase tracking-wide">Availability</p>
                    <p className="text-zg-ink">{viewingLead.availability}</p>
                  </div>
                )}
                <div>
                  <p className="text-zg-muted2 text-xs uppercase tracking-wide">Email</p>
                  <p className="text-zg-ink">{viewingLead.email}</p>
                </div>
                <div>
                  <p className="text-zg-muted2 text-xs uppercase tracking-wide">Phone</p>
                  <p className="text-zg-ink">{viewingLead.phone}</p>
                </div>
                {viewingLead.location && (
                  <div>
                    <p className="text-zg-muted2 text-xs uppercase tracking-wide">Location</p>
                    <p className="text-zg-ink">{viewingLead.location}</p>
                  </div>
                )}
                {viewingLead.experience && (
                  <div>
                    <p className="text-zg-muted2 text-xs uppercase tracking-wide">Experience</p>
                    <p className="text-zg-ink">{viewingLead.experience}</p>
                  </div>
                )}
              </div>
              {viewingLead.message && (
                <div>
                  <p className="text-zg-muted2 text-xs uppercase tracking-wide mb-1">Message</p>
                  <p className="text-zg-ink whitespace-pre-wrap">{viewingLead.message}</p>
                </div>
              )}
              <div>
                <p className="text-zg-muted2 text-xs uppercase tracking-wide">Submitted</p>
                <p className="text-zg-ink">{formatDate(viewingLead.created_at)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete lead confirmation */}
      <AlertDialog open={!!leadDeleteTarget} onOpenChange={(open) => !open && setLeadDeleteTarget(null)}>
        <AlertDialogContent className="rounded-[18px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove {leadDeleteTarget?.full_name}'s partnership application. This can't be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleLeadDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add / Edit vendor form */}
      <Dialog open={vendorFormOpen} onOpenChange={setVendorFormOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto rounded-[18px]">
          <DialogHeader>
            <DialogTitle>{editingVendorId ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleVendorFormSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input
                  value={vendorFormValues.name}
                  onChange={(e) => handleVendorFieldChange('name', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Country</Label>
                <Combobox
                  value={vendorFormValues.country}
                  onChange={(value) => handleVendorFieldChange('country', value)}
                  options={COUNTRIES}
                  placeholder="Select country..."
                  emptyText="No matching country."
                  clearable
                />
              </div>
              <div className="space-y-1.5">
                <Label>Person of Contact</Label>
                <Input
                  value={vendorFormValues.person_of_contact}
                  onChange={(e) => handleVendorFieldChange('person_of_contact', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={vendorFormValues.email}
                  onChange={(e) => handleVendorFieldChange('email', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Phone Number</Label>
                <Input
                  value={vendorFormValues.phone_number}
                  onChange={(e) => handleVendorFieldChange('phone_number', e.target.value)}
                />
              </div>
              <div className="pt-2 border-t border-zg-border space-y-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-zg-muted2">
                  Agency Portal Access
                </p>
                <div className="space-y-1.5">
                  <Label>Username</Label>
                  <Input
                    value={vendorFormValues.username}
                    onChange={(e) => handleVendorFieldChange('username', e.target.value)}
                    placeholder="Leave blank to disable portal login"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{editingVendorId && vendorFormValues.username ? 'New Password (leave blank to keep current)' : 'Password'}</Label>
                  <div className="relative">
                    <Input
                      type={showVendorPassword ? 'text' : 'password'}
                      value={vendorFormValues.password}
                      onChange={(e) => handleVendorFieldChange('password', e.target.value)}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowVendorPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-zg-muted hover:text-zg-ink"
                      tabIndex={-1}
                      aria-label={showVendorPassword ? 'Hide password' : 'Show password'}
                    >
                      {showVendorPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {editingVendorId && vendorFormValues.username && (
                    <p className="text-xs text-zg-muted2">
                      For security, existing passwords aren't stored in a readable form and can't be
                      displayed here - type a new one to change it, or leave this blank to keep the
                      current password.
                    </p>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <Label>Active</Label>
                  <Switch
                    checked={vendorFormValues.is_active}
                    onCheckedChange={(checked) => handleVendorFieldChange('is_active', checked)}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={vendorSaving}>
                {vendorSaving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Vendor login history */}
      <Dialog open={!!loginHistoryVendor} onOpenChange={(open) => !open && setLoginHistoryVendor(null)}>
        <DialogContent className="max-w-md rounded-[18px]">
          <DialogHeader>
            <DialogTitle>{loginHistoryVendor?.name} — Login History</DialogTitle>
          </DialogHeader>
          {loginHistoryLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 text-zg-muted animate-spin" />
            </div>
          ) : loginHistoryEvents.length === 0 ? (
            <p className="text-sm text-zg-muted py-6 text-center">No logins recorded yet.</p>
          ) : (
            <div className="max-h-96 overflow-y-auto space-y-3">
              {loginHistoryEvents.map((event) => (
                <div key={event.id} className="text-sm border-b border-zg-border pb-3 last:border-0">
                  <p className="text-zg-ink font-medium">{formatDate(event.logged_in_at)}</p>
                  <p className="text-zg-muted2 text-xs mt-0.5">
                    {event.ip_address || 'Unknown IP'}
                    {event.user_agent ? ` · ${event.user_agent}` : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm before opening the credentials composer */}
      <AlertDialog open={!!credentialsConfirmTarget} onOpenChange={(open) => !open && setCredentialsConfirmTarget(null)}>
        <AlertDialogContent className="rounded-[18px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Send login credentials to {credentialsConfirmTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll be able to review and edit the subject and message before anything is actually sent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={proceedToComposeCredentials}>Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send Credentials composer */}
      <Dialog open={!!credentialsVendor} onOpenChange={(open) => !open && closeCredentialsDialog()}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto rounded-[18px]">
          <DialogHeader>
            <DialogTitle>Send Login Credentials — {credentialsVendor?.name}</DialogTitle>
          </DialogHeader>
          {credentialsLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 text-zg-muted animate-spin" />
            </div>
          ) : (
            credentialsInfo && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label>Subject Line</Label>
                    {!credentialsInfo.vendor_has_valid_email && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-xs"
                        onClick={() => copyToClipboard(credentialsSubject, 'Subject')}
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copy
                      </Button>
                    )}
                  </div>
                  <Input
                    value={credentialsSubject}
                    onChange={(e) => setCredentialsSubject(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label>Message</Label>
                    {!credentialsInfo.vendor_has_valid_email && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-xs"
                        onClick={() => copyToClipboard(credentialsMessageBody, 'Message')}
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copy
                      </Button>
                    )}
                  </div>
                  <Textarea
                    value={credentialsMessageBody}
                    onChange={(e) => setCredentialsMessageBody(e.target.value)}
                    rows={9}
                  />
                </div>

                {credentialsInfo.vendor_has_valid_email ? (
                  <DialogFooter>
                    <Button onClick={handleSendCredentialsEmail} disabled={credentialsSending}>
                      {credentialsSending ? 'Sending...' : 'Send Email'}
                    </Button>
                  </DialogFooter>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-zg-muted">
                      This vendor has no email on file. Copy the subject and message above and send
                      them manually, then confirm once it's been delivered.
                    </p>
                    <DialogFooter>
                      <Button onClick={handleMarkCredentialsSharedManually} disabled={credentialsSending}>
                        Mark as Manually Sent
                      </Button>
                    </DialogFooter>
                  </div>
                )}
              </div>
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Delete vendor confirmation */}
      <AlertDialog open={!!vendorDeleteTarget} onOpenChange={(open) => !open && setVendorDeleteTarget(null)}>
        <AlertDialogContent className="rounded-[18px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this vendor?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {vendorDeleteTarget?.name}. Any applications linked to this vendor will keep their
              other data but no longer show a vendor. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleVendorDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add / Edit client form */}
      <Dialog open={clientFormOpen} onOpenChange={setClientFormOpen}>
        <DialogContent className="max-w-md rounded-[18px]">
          <DialogHeader>
            <DialogTitle>{editingClientId ? 'Edit Client' : 'Add Client'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleClientFormSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input
                  value={clientFormValues.name}
                  onChange={(e) => handleClientFieldChange('name', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Country</Label>
                <Combobox
                  value={clientFormValues.country}
                  onChange={(value) => handleClientFieldChange('country', value)}
                  options={COUNTRIES}
                  placeholder="Select country..."
                  emptyText="No matching country."
                  clearable
                />
              </div>
              <div className="space-y-1.5">
                <Label>Person of Contact</Label>
                <Input
                  value={clientFormValues.person_of_contact}
                  onChange={(e) => handleClientFieldChange('person_of_contact', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={clientFormValues.email}
                  onChange={(e) => handleClientFieldChange('email', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Phone Number</Label>
                <Input
                  value={clientFormValues.phone_number}
                  onChange={(e) => handleClientFieldChange('phone_number', e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={clientSaving}>
                {clientSaving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete client confirmation */}
      <AlertDialog open={!!clientDeleteTarget} onOpenChange={(open) => !open && setClientDeleteTarget(null)}>
        <AlertDialogContent className="rounded-[18px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this client?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {clientDeleteTarget?.name}. Any applications linked to this client will keep their
              other data but no longer show a client. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClientDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
